import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  Send,
  Eye,
  EyeOff,
  Copy,
  CheckCircle2,
  Radio,
  Server,
  Layers,
  Loader2,
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";
import "./SmsGateway.css";

const API_BASE_URL = "https://payyug.in/api";

const initialForm = {
  gatewayName: "",
  gatewayCode: "",
  apiBaseUrl: "",
  apiKey: "",
  apiSecret: "",
  senderId: "",
  entityId: "",
  authType: "API_KEY",
  isActive: true,
};

const SmsGateway = () => {
  const [gateways, setGateways] = useState([]);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Modals & Visibility State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState({});

  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});

  // Super Admin ID safely from localStorage
  const superAdminId = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return localStorage.getItem("adminuserId") || user?.superAdminId || user?.id || undefined;
    } catch {
      return localStorage.getItem("adminuserId") || undefined;
    }
  }, []);

const fetchGateways = useCallback(async () => {
  setIsTableLoading(true);
  try {
    const response = await axios.get(`${API_BASE_URL}/sms-getway/list`, {
      params: {
        superAdminId,
        page: currentPage,
        limit: rowsPerPage,
      },
    });

    const resData = response?.data;
    if (
      resData?.status === "SUCCESS" ||
      resData?.success === true ||
      Array.isArray(resData?.data)
    ) {
      const list = resData?.data || resData?.gateways || [];
      setGateways(list);
      setTotalPages(resData?.totalPages || 1);
      setTotalRecords(resData?.totalItems || resData?.totalRecords || list.length);
    } else {
      setGateways(resData?.data || []);
    }
  } catch (error) {
    console.error("Fetch Gateways Error:", error);
    toast.error(error?.response?.data?.message || "Failed to load SMS gateways.");
  } finally {
    setIsTableLoading(false);
  }
}, [superAdminId, currentPage, rowsPerPage]);

useEffect(() => {
  fetchGateways();
}, [fetchGateways]);

  // Stats Calculations
  const stats = useMemo(() => {
    const total = totalRecords || gateways.length;
    const active = gateways.filter((g) => g.isActive === true).length;
    const inactive = total - active;
    return { total, active, inactive, uptime: "99.9%" };
  }, [gateways, totalRecords]);

  // Key Visibility Toggle
  const toggleKeyVisibility = (id) => {
    setVisibleKeys((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Copy API Key
  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("API Key copied to clipboard!");
  };

// Backend se aayi list par Search aur Status filter frontend se apply hoga
const filteredGateways = useMemo(() => {
  return gateways.filter((item) => {
    const name = item?.gatewayName || "";
    const code = item?.gatewayCode || "";
    const sender = item?.senderId || "";
    const id = String(item?.id || item?._id || "");

    const query = searchTerm.toLowerCase().trim();
    const matchesSearch =
      !query ||
      name.toLowerCase().includes(query) ||
      code.toLowerCase().includes(query) ||
      sender.toLowerCase().includes(query) ||
      id.toLowerCase().includes(query);

    const matchesStatus =
      statusFilter === "ALL" ||
      (statusFilter === "ACTIVE" ? item.isActive === true : item.isActive === false);

    return matchesSearch && matchesStatus;
  });
}, [gateways, searchTerm, statusFilter]);

const handlePageChange = (newPage) => {
  if (newPage >= 1 && newPage <= totalPages) {
    setCurrentPage(newPage);
  }
};

  // Form Validation
  const validateForm = () => {
    const errors = {};

    if (!formData.gatewayName.trim()) {
      errors.gatewayName = "Gateway Name is required";
    }

    if (!formData.gatewayCode.trim()) {
      errors.gatewayCode = "Gateway Code is required";
    }

    if (!formData.senderId.trim()) {
      errors.senderId = "Sender ID is required";
    } else if (formData.senderId.trim().length !== 6) {
      errors.senderId = "Sender ID must be exactly 6 characters";
    }

    // if (!formData.apiKey.trim()) {
    //   errors.apiKey = "API Key is required";
    // }

    if (!formData.apiBaseUrl.trim()) {
      errors.apiBaseUrl = "API Base URL is required";
    } else if (!/^https?:\/\/.+/.test(formData.apiBaseUrl.trim())) {
      errors.apiBaseUrl = "Must be a valid HTTP/HTTPS URL";
    }

    // if (formData.authType === "BEARER_TOKEN" && !formData.apiSecret.trim()) {
    //   errors.apiSecret = "API Secret is required for Bearer Token auth";
    // }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 2. CREATE / 3. UPDATE Gateway Handler
  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill all required fields correctly.");
      return;
    }

    setIsActionLoading(true);

    const payload = {
      superAdminId: Number(superAdminId) || superAdminId,
      gatewayName: formData.gatewayName.trim(),
      gatewayCode: formData.gatewayCode.toUpperCase().trim(),
      apiBaseUrl: formData.apiBaseUrl.trim(),
      apiKey: formData.apiKey.trim(),
      apiSecret: formData.apiSecret.trim(),
      senderId: formData.senderId.toUpperCase().trim(),
      entityId: formData.entityId.trim(),
      authType: formData.authType,
      isActive: Boolean(formData.isActive),
      priority: 1,
    };

    try {
      if (editingId) {
        const response = await axios.put(
          `${API_BASE_URL}/sms-getway/${editingId}`,
          payload
        );
        const resData = response?.data;

        if (resData?.status === "SUCCESS" || resData?.success === true || response.status === 200) {
          toast.success(resData?.message || "Gateway configuration updated!");
          fetchGateways();
          closeModal();
        } else {
          toast.error(resData?.message || "Failed to update gateway.");
        }
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/sms-getway/create`,
          payload
        );
        const resData = response?.data;

        if (resData?.status === "SUCCESS" || resData?.success === true || response.status === 201 || response.status === 200) {
          toast.success(resData?.message || "New SMS Gateway connected!");
          fetchGateways();
          closeModal();
        } else {
          toast.error(resData?.message || "Failed to create gateway.");
        }
      }
    } catch (error) {
      console.error("Save Gateway Error:", error);
      toast.error(error?.response?.data?.message || error?.message || "Something went wrong.");
    } finally {
      setIsActionLoading(false);
    }
  };

  // Open Add/Edit Modal
  const openModal = (gateway = null) => {
    if (gateway) {
      const gId = gateway.id || gateway._id;
      setEditingId(gId);
      setFormData({
        gatewayName: gateway.gatewayName || "",
        gatewayCode: gateway.gatewayCode || "",
        apiBaseUrl: gateway.apiBaseUrl || "",
        apiKey: gateway.apiKey || "",
        apiSecret: gateway.apiSecret || "",
        senderId: gateway.senderId || "",
        entityId: gateway.entityId || "",
        authType: gateway.authType || "API_KEY",
        isActive: gateway.isActive !== undefined ? gateway.isActive : true,
      });
    } else {
      setEditingId(null);
      setFormData(initialForm);
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
    setFormData(initialForm);
    setFormErrors({});
  };

  // 4. DELETE Handler
  const handleDelete = async (gateway) => {
    const gId = gateway.id || gateway._id;
    if (!window.confirm(`Are you sure you want to permanently delete "${gateway.gatewayName}"?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/sms-getway/${gId}`, {
        params: { superAdminId },
      });
      const resData = response?.data;

      if (resData?.status === "SUCCESS" || resData?.success === true || response.status === 200) {
        toast.success(resData?.message || "Gateway removed successfully.");
        setGateways((prev) => prev.filter((item) => (item.id || item._id) !== gId));
      } else {
        toast.error(resData?.message || "Failed to delete gateway.");
      }
    } catch (error) {
      console.error("Delete Gateway Error:", error);
      toast.error(error?.response?.data?.message || "Failed to delete gateway.");
    }
  };

  return (
    <div className="sms-gw-container">
      {/* Top Banner */}
      <div className="sms-gw-header">
        <div className="sms-gw-header-info">
          <h1>SMS Gateway</h1>
          <p>Configure HTTP endpoints, sender headers, and live SMS delivery routes.</p>
        </div>
        <div className="sms-gw-header-actions">
          <button
            onClick={() => openModal()}
            className="sms-gw-btn-primary"
            type="button"
          >
            <Plus size={16} />
            <span>Connect Gateway</span>
          </button>
        </div>
      </div>

      {/* KPI Stats Grid */}
      <div className="sms-gw-stats-grid">
        <div className="sms-gw-stat-card">
          <div className="sms-gw-stat-icon blue">
            <Server size={22} />
          </div>
          <div className="sms-gw-stat-details">
            <h4>Total Gateways</h4>
            <div className="sms-gw-stat-value">{stats.total}</div>
          </div>
        </div>

        <div className="sms-gw-stat-card">
          <div className="sms-gw-stat-icon green">
            <CheckCircle2 size={22} />
          </div>
          <div className="sms-gw-stat-details">
            <h4>Active Routes</h4>
            <div className="sms-gw-stat-value">{stats.active}</div>
          </div>
        </div>

        <div className="sms-gw-stat-card">
          <div className="sms-gw-stat-icon amber">
            <Layers size={22} />
          </div>
          <div className="sms-gw-stat-details">
            <h4>Inactive Routes</h4>
            <div className="sms-gw-stat-value">{stats.inactive}</div>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="sms-gw-card">
        {/* Toolbar & Filters */}
        <div className="sms-gw-toolbar">
          <div className="sms-gw-search-wrapper">
            <Search size={16} className="sms-gw-search-icon" />
            <input
              type="text"
              placeholder="Search by gateway name, code, sender ID..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="sms-gw-search-input"
            />
          </div>

          <div className="sms-gw-filter-tabs">
            <button
              onClick={() => {
                setStatusFilter("ALL");
                setCurrentPage(1);
              }}
              className={`sms-gw-tab-btn ${statusFilter === "ALL" ? "active" : ""}`}
              type="button"
            >
              <span>All</span>
              <span className="sms-gw-tab-count">{stats.total}</span>
            </button>
            <button
              onClick={() => {
                setStatusFilter("ACTIVE");
                setCurrentPage(1);
              }}
              className={`sms-gw-tab-btn ${statusFilter === "ACTIVE" ? "active" : ""}`}
              type="button"
            >
              <span>Active</span>
              <span className="sms-gw-tab-count">{stats.active}</span>
            </button>
            <button
              onClick={() => {
                setStatusFilter("INACTIVE");
                setCurrentPage(1);
              }}
              className={`sms-gw-tab-btn ${statusFilter === "INACTIVE" ? "active" : ""}`}
              type="button"
            >
              <span>Inactive</span>
              <span className="sms-gw-tab-count">{stats.inactive}</span>
            </button>
          </div>
        </div>

        {/* Responsive Data Table */}
        <div className="sms-gw-table-wrap">
          <table className="sms-gw-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Gateway Details</th>
                <th>Sender ID & DLT</th>
                <th>Base API URL</th>
                {/* <th>API Key / Auth</th> */}
                {/* <th>Priority</th> */}
                <th>Status</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
  {isTableLoading ? (
    <tr>
      <td colSpan={7} className="sms-gw-empty">
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.5rem" }}>
          <Loader2 size={18} className="animate-spin" />
          <span>Loading SMS gateways...</span>
        </div>
      </td>
    </tr>
  ) : filteredGateways.length > 0 ? (
                filteredGateways.map((row,i) => {
                  const rowId = row.id || row._id || row.gatewayCode;
                  return (
                    <tr key={rowId}>
                      <td>{(currentPage - 1) * rowsPerPage + i + 1}</td>
                      <td>
                        <div className="sms-gw-provider-meta">
                          <div>
                            <div className="sms-gw-provider-name">{row.gatewayName}</div>
                            <div className="sms-gw-provider-id">{row.gatewayCode}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          <span className="sms-gw-sender-badge">
                            <Radio size={13} color="#2563eb" />
                            {row.senderId}
                          </span>
                          {row.entityId && (
                            <span style={{ fontSize: "0.6875rem", color: "#64748b", fontFamily: "monospace" }}>
                              DLT: {row.entityId}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span
                          style={{
                            maxWidth: "14rem",
                            display: "inline-block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "#64748b",
                          }}
                        >
                          {row.apiBaseUrl}
                        </span>
                      </td>
                      {/* <td>
                        <div className="sms-gw-key-box">
                          <span>
                            {visibleKeys[rowId]
                              ? row.apiKey
                              : `${row.apiKey ? row.apiKey.slice(0, 5) : "•••••"}••••••••`}
                          </span>
                          <button
                            onClick={() => toggleKeyVisibility(rowId)}
                            className="sms-gw-key-btn"
                            title={visibleKeys[rowId] ? "Hide Key" : "Reveal Key"}
                            type="button"
                          >
                            {visibleKeys[rowId] ? <EyeOff size={13} /> : <Eye size={13} />}
                          </button>
                          <button
                            onClick={() => copyToClipboard(row.apiKey)}
                            className="sms-gw-key-btn"
                            title="Copy Key"
                            type="button"
                          >
                            <Copy size={13} />
                          </button>
                        </div>
                      </td> */}
                      {/* <td>
                        <span className="sms-gw-priority-tag">
                          Priority {row.priority || 1}
                        </span>
                      </td> */}
                      <td>
                        <span className={`sms-gw-badge ${row.isActive ? "active" : "inactive"}`}>
                          <span className="sms-gw-dot" />
                          {row.isActive ? "ACTIVE" : "INACTIVE"}
                        </span>
                      </td>
                      <td>
                        <div className="sms-gw-actions-cell">
                          {/* Edit Gateway */}
                          <button
                            onClick={() => openModal(row)}
                            className="sms-gw-icon-btn"
                            title="Edit Configuration"
                            type="button"
                          >
                            <Edit2 size={14} />
                          </button>

                          {/* Delete Gateway */}
                          <button
                            onClick={() => handleDelete(row)}
                            className="sms-gw-icon-btn danger"
                            title="Delete Gateway"
                            type="button"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="sms-gw-empty">
                    No SMS Gateways found matching your filter criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="sms-gw-pagination-bar">
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span>Rows:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>
              Showing {filteredGateways.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, totalRecords || filteredGateways.length)} of{" "}
              {totalRecords || filteredGateways.length}
            </span>
          </div>

          <div className="sms-gw-page-numbers">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="sms-gw-page-btn"
              type="button"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`sms-gw-page-btn ${currentPage === page ? "active" : ""}`}
                type="button"
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="sms-gw-page-btn"
              type="button"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Connect / Edit Modal */}
      {isModalOpen && (
        <div className="sms-gw-modal-backdrop" onClick={closeModal}>
          <div className="sms-gw-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="sms-gw-modal-head">
              <h3>{editingId ? "Edit Gateway Route" : "Connect SMS Provider"}</h3>
              <button
                onClick={closeModal}
                className="sms-gw-close-btn"
                type="button"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSave} className="sms-gw-modal-form-wrapper">
              <div className="sms-gw-modal-form-body">
                {/* Gateway Name & Gateway Code */}
                <div className="sms-gw-form-row">
                  <div className="sms-gw-field-group">
                    <label>
                      Gateway Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      // placeholder="e.g. Fast2SMS"
                      value={formData.gatewayName}
                      onChange={(e) =>
                        setFormData({ ...formData, gatewayName: e.target.value })
                      }
                    />
                    {formErrors.gatewayName && (
                      <p className="sms-gw-error-hint">{formErrors.gatewayName}</p>
                    )}
                  </div>

                  <div className="sms-gw-field-group">
                    <label>
                      Gateway Code <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      // placeholder="e.g. FAST2SMS"
                      value={formData.gatewayCode}
                      onChange={(e) =>
                        setFormData({ ...formData, gatewayCode: e.target.value.toUpperCase() })
                      }
                    />
                    {formErrors.gatewayCode && (
                      <p className="sms-gw-error-hint">{formErrors.gatewayCode}</p>
                    )}
                  </div>
                </div>

                {/* API Base URL */}
                <div className="sms-gw-field-group">
                  <label>
                    API Base URL <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    type="text"
                    // placeholder="https://www.fast2sms.com/dev/bulkV2"
                    value={formData.apiBaseUrl}
                    onChange={(e) =>
                      setFormData({ ...formData, apiBaseUrl: e.target.value })
                    }
                  />
                  {formErrors.apiBaseUrl && (
                    <p className="sms-gw-error-hint">{formErrors.apiBaseUrl}</p>
                  )}
                </div>

                {/* Sender ID & DLT Entity ID */}
                <div className="sms-gw-form-row">
                  <div className="sms-gw-field-group">
                    <label>
                      Sender ID (Header) <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={6}
                      // placeholder="e.g. FSTSMS (6 chars)"
                      value={formData.senderId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          senderId: e.target.value.toUpperCase(),
                        })
                      }
                    />
                    {formErrors.senderId && (
                      <p className="sms-gw-error-hint">{formErrors.senderId}</p>
                    )}
                  </div>

                  <div className="sms-gw-field-group">
                    <label>Entity ID (DLT Principal)</label>
                    <input
                      type="text"
                      // placeholder="e.g. 120XXXXXXXX"
                      value={formData.entityId}
                      onChange={(e) =>
                        setFormData({ ...formData, entityId: e.target.value })
                      }
                    />
                  </div>
                </div>

                {/* API Key */}
                <div className="sms-gw-field-group">
                  <label>
                    API Key / Access Token
                  </label>
                  <input
                    type="text"
                    // placeholder="Enter API key"
                    value={formData.apiKey}
                    onChange={(e) =>
                      setFormData({ ...formData, apiKey: e.target.value })
                    }
                  />
                  {/* {formErrors.apiKey && (
                    <p className="sms-gw-error-hint">{formErrors.apiKey}</p>
                  )} */}
                </div>

                {/* API Secret */}
                <div className="sms-gw-field-group">
                  <label>API Secret</label>
                  <input
                    type="text"
                    // placeholder="Enter API secret if required"
                    value={formData.apiSecret}
                    onChange={(e) =>
                      setFormData({ ...formData, apiSecret: e.target.value })
                    }
                  />
                  {/* {formErrors.apiSecret && (
                    <p className="sms-gw-error-hint">{formErrors.apiSecret}</p>
                  )} */}
                </div>

                {/* Auth Type & Priority */}
                <div className="sms-gw-form-row">
                  <div className="sms-gw-field-group">
                    <label>Auth Type</label>
                    <select
                      value={formData.authType}
                      onChange={(e) =>
                        setFormData({ ...formData, authType: e.target.value })
                      }
                    >
                      <option value="API_KEY">API_KEY (Header / Param)</option>
                      <option value="BEARER_TOKEN">BEARER_TOKEN (Bearer Auth)</option>
                      <option value="BASIC_AUTH">BASIC_AUTH (Key + Secret)</option>
                    </select>
                  </div>

                  <div className="sms-gw-field-group">
                  <label>Route Status</label>
                  <select
                    value={formData.isActive ? "true" : "false"}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.value === "true" })
                    }
                  >
                    <option value="true">Active (Live Dispatch)</option>
                    <option value="false">Inactive (Disabled)</option>
                  </select>
                </div>
                </div>
                
              </div>

              <div className="sms-gw-modal-foot">
                <button
                  type="button"
                  onClick={closeModal}
                  className="sms-gw-btn-secondary"
                  disabled={isActionLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="sms-gw-btn-primary"
                  disabled={isActionLoading}
                >
                  {isActionLoading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Send size={14} />
                      <span>{editingId ? "Save Changes" : "Save Gateway"}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmsGateway;